import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  FileText, Edit3, Expand, Smile, Tag, Image, Upload, MessageCircle,
  Sheet, MapPin, Sparkles, FolderOpen, Paperclip, Save, Minimize2,
  Eye, List, CheckSquare, HelpCircle, FilePlus, Loader,
  BarChart2, Filter, Hash, Search, Table, Download,
} from 'lucide-react';
import { mockApiCall } from '../api/mockApi';

// ---------------------------------------------------------------------------
// Icon mapping for AI suggestion icon_hint strings
// ---------------------------------------------------------------------------
const ICON_MAP = {
  'file-text':   <FileText size={16} />,
  'maximize-2':  <Expand size={16} />,
  'minimize-2':  <Minimize2 size={16} />,
  'tag':         <Tag size={16} />,
  'smile':       <Smile size={16} />,
  'image':       <Image size={16} />,
  'image-plus':  <Image size={16} />,
  'eye':         <Eye size={16} />,
  'list':        <List size={16} />,
  'check-square':<CheckSquare size={16} />,
  'help-circle': <HelpCircle size={16} />,
  'file-plus':   <FilePlus size={16} />,
  'edit-3':      <Edit3 size={16} />,
  'sparkles':    <Sparkles size={16} />,
  'bar-chart-2': <BarChart2 size={16} />,
  'filter':      <Filter size={16} />,
  'hash':        <Hash size={16} />,
  'search':      <Search size={16} />,
  'table':       <Table size={16} />,
  'download':    <Download size={16} />,
};
const iconFor = (hint) => ICON_MAP[hint] || <Sparkles size={16} />;

// ---------------------------------------------------------------------------
// Tier-1 instant suggestions — no LLM, purely file-type heuristics
// Returns [{label, prompt, icon_hint}] shown immediately while LLM loads
// ---------------------------------------------------------------------------
function getTier1Suggestions(nodeData) {
  const mime = nodeData?.mime_type || '';
  const schema = nodeData?.schema_preview || '';
  const filename = nodeData?.filename || nodeData?.title || '';

  // Extract first numeric-looking column name from CSV schema for "top N" suggestion
  const csvColMatch = schema.match(/columns \[([^\]]+)\]/);
  const csvCols = csvColMatch ? csvColMatch[1].split(',').map((c) => c.trim()) : [];
  const numericCol = csvCols.find((c) => /price|value|amount|total|count|volume|high|low|close|open|revenue|cost|qty|quantity/i.test(c)) || csvCols[1] || csvCols[0];

  if (mime.includes('csv') || mime.includes('tab-separated') || filename.endsWith('.csv') || filename.endsWith('.tsv')) {
    return [
      { label: 'Summarise key statistics', prompt: 'Summarise the key statistics of this dataset — row count, column names, data types, and any notable values.', icon_hint: 'file-text' },
      numericCol ? { label: `Top 10 by ${numericCol}`, prompt: `Give me a table of the top 10 rows ranked by ${numericCol}, highest first.`, icon_hint: 'bar-chart-2' }
                 : { label: 'Show first 10 rows as table', prompt: 'Show the first 10 rows formatted as a readable table.', icon_hint: 'list' },
      { label: 'List column names and types', prompt: 'List all column names and infer their data types (numeric, text, date, etc.).', icon_hint: 'hash' },
      { label: 'Find outliers or anomalies', prompt: 'Identify any outliers, anomalies, or unusual values in this dataset.', icon_hint: 'search' },
    ];
  }

  if (mime.includes('json') || filename.endsWith('.json')) {
    return [
      { label: 'Summarise structure', prompt: 'Summarise the structure and key fields of this JSON document.', icon_hint: 'file-text' },
      { label: 'Extract all top-level keys', prompt: 'List all top-level keys and their value types.', icon_hint: 'hash' },
      { label: 'Find interesting patterns', prompt: 'What interesting patterns or relationships can you find in this data?', icon_hint: 'search' },
    ];
  }

  if (mime.startsWith('text/') || filename.endsWith('.txt') || filename.endsWith('.md')) {
    return [
      { label: 'Summarise', prompt: 'Summarise the key points of this document.', icon_hint: 'minimize-2' },
      { label: 'Extract key points', prompt: 'Extract and list the most important points from this document.', icon_hint: 'list' },
      { label: 'Identify topics', prompt: 'What are the main topics covered in this document?', icon_hint: 'tag' },
    ];
  }

  return [];
}

// ---------------------------------------------------------------------------
// Fixed per-node-type actions (always present, no AI involvement)
// ---------------------------------------------------------------------------
const FIXED_ACTIONS = {
  textNode: [
    { label: 'Summary',    operation: 'summary',    icon: <FileText size={16} /> },
    { label: 'Expand',     operation: 'expand',     icon: <Expand size={16} /> },
    { label: 'Short-form', operation: 'short-form', icon: <Minimize2 size={16} /> },
    { label: 'Classify',   operation: 'classify',   icon: <Tag size={16} /> },
    { label: 'Sentiment',  operation: 'sentiment',  icon: <Smile size={16} /> },
  ],
  imageNode: [
    { label: 'Describe', operation: 'describe', icon: <Eye size={16} /> },
    { label: 'Tag',      operation: 'tag',      icon: <Tag size={16} /> },
    { label: 'Classify', operation: 'classify', icon: <Tag size={16} /> },
    { label: 'Sentiment',operation: 'sentiment',icon: <Smile size={16} /> },
  ],
  promptNode: [
    { label: 'Submit Prompt', operation: 'submit-prompt', icon: <FileText size={16} /> },
  ],
  fileNode: [],   // suggestions only — Tier-1 shown in suggestions section
  root: [],
  pane: [],
  workflowNode: [],
};

// Always-present top actions (above the divider, every node type)
const TOP_ACTIONS = [
  { label: 'Save branch as Flow', operation: 'save-as-flow', icon: <Save size={16} />, alwaysOn: true },
  { label: 'Upload & Attach File', operation: 'upload-attach-file', icon: <Paperclip size={16} /> },
  { label: 'Select from Library',  operation: 'select-from-library', icon: <FolderOpen size={16} /> },
];

// Additional top actions only shown for specific types
const TOP_ACTIONS_BY_TYPE = {
  root: [
    { label: 'Load File',  operation: 'load-file',  icon: <Upload size={16} /> },
    { label: 'Load Image', operation: 'load-image', icon: <Upload size={16} /> },
  ],
  textNode: [
    { label: 'Load Image',        operation: 'load-image',     icon: <Upload size={16} /> },
    { label: 'Create Action',     operation: 'create-action',  icon: <MessageCircle size={16} /> },
    { label: 'Create Map',        operation: 'create-map',     icon: <MapPin size={16} /> },
    { label: 'Google Sheets',     operation: 'google-sheets',  icon: <Sheet size={16} /> },
    { label: 'Generate Image',    operation: 'generate-image', icon: <Image size={16} /> },
    { label: 'New Text Node',     operation: 'new-text-node',  icon: <FileText size={16} /> },
    { label: 'New Image Node',    operation: 'new-image-node', icon: <Image size={16} /> },
  ],
  imageNode: [
    { label: 'Load Image',     operation: 'load-image',     icon: <Upload size={16} /> },
    { label: 'Create Action',  operation: 'create-action',  icon: <MessageCircle size={16} /> },
    { label: 'Create Map',     operation: 'create-map',     icon: <MapPin size={16} /> },
    { label: 'New Text Node',  operation: 'new-text-node',  icon: <FileText size={16} /> },
    { label: 'New Image Node', operation: 'new-image-node', icon: <Image size={16} /> },
  ],
  promptNode: [
    { label: 'New Text Node',  operation: 'new-text-node',  icon: <FileText size={16} /> },
    { label: 'New Image Node', operation: 'new-image-node', icon: <Image size={16} /> },
  ],
};

// ---------------------------------------------------------------------------
// ContextMenu component
// ---------------------------------------------------------------------------
const ContextMenu = ({
  x, y,
  nodeType,
  role,
  onCreateNode,
  onCreateAction,
  onAnalyzeWorkflow,
  onUploadAttachFile,
  onSelectFromLibrary,
  onClose,
  onSuggestPrefill,  // (promptText) => void — pre-fills chat bar with suggestion
  nodeData,
  autoClassify,
  autoSentiment,
  taskDescription,
  aiSuggestions,   // null = still loading | [] = empty | [{label,operation,icon_hint,...}]
}) => {
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: y, left: x });
  const [showAll, setShowAll] = useState(false);

  useLayoutEffect(() => {
    if (!menuRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = menuRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const MARGIN = 8;
    setMenuPos({
      top:  y + h + MARGIN > vh ? Math.max(MARGIN, vh - h - MARGIN) : y,
      left: x + w + MARGIN > vw ? Math.max(MARGIN, vw - w - MARGIN) : x,
    });
  }, [x, y]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [onClose]);

  // Classification disabled check
  const isTagDisabled = (type) => {
    if ((type === 'types' && autoClassify) || (type === 'sentiment' && autoSentiment)) return true;
    const tags = nodeData?.tags || [];
    const lock = nodeData?.classificationLocks?.[type];
    const hasTag = tags.some((t) => t.type === type);
    return hasTag && lock === nodeData?.content;
  };

  // Build fixed section
  const fixedActions = (FIXED_ACTIONS[nodeType] || []).map((item) => {
    if (item.operation === 'classify') return { ...item, disabled: isTagDisabled('types') };
    if (item.operation === 'sentiment') return { ...item, disabled: isTagDisabled('sentiment') };
    return item;
  });

  // Build top section (always-on + type-specific extras, collapsed by default)
  const topAlwaysOn = TOP_ACTIONS;
  const typeExtras = TOP_ACTIONS_BY_TYPE[nodeType] || [];
  const allTopActions = [...topAlwaysOn, ...typeExtras];
  const visibleTopActions = showAll ? allTopActions : topAlwaysOn;

  // Tier-1 instant suggestions (file nodes only — no LLM needed)
  const tier1 = nodeType === 'fileNode' ? getTier1Suggestions(nodeData) : [];

  // AI suggestions section
  const suggestionsLoading = aiSuggestions === null;
  const hasSuggestions = Array.isArray(aiSuggestions) && aiSuggestions.length > 0;

  // Handle item click
  const handleClick = async (operation, label, extra = {}) => {
    switch (operation) {
      case 'save-as-flow':
        // TODO: wire to save-branch-as-flow handler (p27.7)
        onClose();
        return;

      case 'analyze-workflow': {
        const content = nodeType === 'imageNode' ? nodeData?.imageUrl : nodeData?.content;
        if (onAnalyzeWorkflow && content) onAnalyzeWorkflow(content);
        onClose();
        return;
      }

      case 'upload-attach-file':
        if (onUploadAttachFile) onUploadAttachFile();
        onClose();
        return;

      case 'select-from-library':
        if (onSelectFromLibrary) onSelectFromLibrary();
        onClose();
        return;

      case 'load-image': {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (event) => {
          const file = event.target.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (e) => {
            onCreateNode({ operation, label, imageUrl: e.target.result });
          };
          reader.readAsDataURL(file);
        };
        input.click();
        return;
      }

      case 'load-file': {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.txt,.md,.json';
        input.onchange = (event) => {
          const file = event.target.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (e) => {
            onCreateNode({ operation, label, content: e.target.result });
          };
          reader.readAsText(file);
        };
        input.click();
        return;
      }

      case 'create-action':
        if (onCreateAction) onCreateAction();
        return;

      case 'submit-prompt':
        if (nodeData?.content?.trim()) {
          try {
            const response = await mockApiCall({
              parentNodeId: nodeData.id,
              parentNodeContent: nodeData.content,
              parentNodeType: 'promptNode',
              operation: 'submit-prompt',
            });
            onCreateNode({ operation, label, content: response.content });
          } catch {}
        }
        return;

      default:
        onCreateNode({ operation, label, ...extra });
    }
    onClose();
  };

  const renderItem = (item, idx) => (
    <div
      key={`${item.operation}-${idx}`}
      className={`context-menu-item${item.disabled ? ' disabled' : ''}${item.highlighted ? ' highlighted' : ''}`}
      onClick={() => !item.disabled && handleClick(item.operation, item.label)}
    >
      {item.icon}
      <span style={{ marginLeft: 8 }}>{item.label}</span>
    </div>
  );

  const Divider = () => <div className="context-menu-divider" />;

  return (
    <div className="context-menu" style={{ top: menuPos.top, left: menuPos.left }} ref={menuRef}>
      {taskDescription && <div className="context-menu-header">{taskDescription}</div>}
      {nodeType === 'fileNode' && nodeData?.schema_preview && (
        <div className="context-menu-schema-preview" title={nodeData.schema_preview}>
          {nodeData.schema_preview.slice(0, 80)}{nodeData.schema_preview.length > 80 ? '…' : ''}
        </div>
      )}

      {/* ── Top: always-on + optional extras ── */}
      {visibleTopActions.map((item, i) => renderItem(item, i))}
      {!showAll && typeExtras.length > 0 && (
        <div
          className="context-menu-item"
          style={{ color: '#888', fontSize: 11 }}
          onClick={() => setShowAll(true)}
        >
          <span style={{ marginLeft: 0 }}>More options…</span>
        </div>
      )}

      {/* ── Fixed node-type actions ── */}
      {fixedActions.length > 0 && (
        <>
          <Divider />
          {fixedActions.map((item, i) => renderItem(item, i))}
        </>
      )}

      {/* ── Suggestions (Tier-1 instant + AI LLM) ── */}
      {(tier1.length > 0 || suggestionsLoading || hasSuggestions) && (
        <>
          <Divider />
          <div className="context-menu-section-label">
            <Sparkles size={11} style={{ marginRight: 4 }} />
            Suggestions
          </div>

          {/* Tier-1: always shown instantly for file nodes */}
          {tier1.map((s, i) => (
            <div
              key={`t1-${i}`}
              className="context-menu-item highlighted"
              onClick={() => {
                if (onSuggestPrefill && s.prompt) onSuggestPrefill(s.prompt);
                onClose();
              }}
              title={s.prompt}
            >
              {iconFor(s.icon_hint)}
              <span style={{ marginLeft: 8 }}>{s.label}</span>
            </div>
          ))}

          {/* LLM suggestions — show spinner while loading, then results (deduplicated vs tier1) */}
          {suggestionsLoading && tier1.length === 0 && (
            <div className="context-menu-item" style={{ color: '#aaa', fontSize: 12 }}>
              <Loader size={13} style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />
              Thinking…
            </div>
          )}
          {hasSuggestions && aiSuggestions.slice(0, 5).map((s, i) => (
            <div
              key={`ai-${i}`}
              className="context-menu-item highlighted"
              onClick={() => {
                if (onSuggestPrefill && s.prompt) {
                  onSuggestPrefill(s.prompt);
                  onClose();
                } else {
                  handleClick(s.operation, s.label);
                }
              }}
              title={s.reasoning || s.prompt || ''}
            >
              {iconFor(s.icon_hint)}
              <span style={{ marginLeft: 8 }}>{s.label}</span>
              {s.confidence >= 0.75 && (
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6366f1', opacity: 0.7 }}>✦</span>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ContextMenu;
